import { colors } from 'baseui/tokens';
import stylesColors from 'common/styles/styles.colors';
import { string2money } from 'common/utils/string';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import Text from 'components/elements/text';
import { SeparatorHorizontal, SeparatorVertical } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  ActivityContainer,
  ActivityContent,
  TotalContent,
  TotalItemContent,
} from './styles';
import SummaryItem from './summary-item';
import { ActivitySaleFormType } from '../form-type';
import useRecalculate from '../hooks/use-recalculate';

interface Props {
  onClose: () => void;
}

function Total({ label, price }: { label: string; price: number }) {
  return (
    <TotalContent>
      <TotalItemContent>
        <Text variant="LabelMedium">{label}:</Text>
        <SeparatorHorizontal $margin={8} />
        <Text variant="HeadingXSmall">{`Rp ${string2money(price)}`}</Text>
      </TotalItemContent>
    </TotalContent>
  );
}

export default function SummaryDrawer(props: Props) {
  const { t } = useTranslation();
  const { onClose } = props;
  const { control } = useFormContext<ActivitySaleFormType>();
  const [activitySales] = useWatch({
    control,
    name: ['activitySales'],
  });

  const { discount, otherCost, total, subtotal, totalAddOns } =
    useRecalculate();

  return (
    <>
      <DrawerHeader onClose={onClose} title={t('common:other_sale_detail')} />
      <DrawerBody>
        {activitySales.map((activitySale, idx) => {
          const isGroupedPrice = activitySale.isGroupedPrice;
          const activityGuests = activitySale.activitySaleGuests || [];
          const activityAddOns = activitySale.activitySaleAddOns || [];

          return (
            <React.Fragment key={activitySale.id}>
              <Text
                variant="LabelMedium"
                $style={{ color: stylesColors.primaryDefault }}
              >
                {t('common:detail_extra', { extra: `${idx + 1}` })}
              </Text>
              <SeparatorVertical $margin={12} />
              {isGroupedPrice ? (
                <>
                  <SummaryItem data={activityGuests} isGrouped />
                  <SeparatorVertical $margin={24} />
                </>
              ) : (
                <>
                  {activityGuests.map((activityGuest, idx) => {
                    const guestAddOns = activityAddOns.filter(
                      (guestAddOn) =>
                        guestAddOn.guestId === activityGuest.guestId,
                    );

                    return (
                      <React.Fragment key={idx}>
                        <SummaryItem data={[activityGuest]} />
                        {guestAddOns.length > 0 && (
                          <>
                            <SeparatorVertical $margin={12} />
                            <Text
                              variant="ParagraphSmall"
                              color={colors.gray500}
                            >
                              {t('ticketSales:add_ons')}
                            </Text>
                            {guestAddOns.map((guestAddOn, idx) => (
                              <ActivityContainer key={idx}>
                                <ActivityContent $style={{ paddingLeft: 0 }}>
                                  <Text variant="LabelSmall">
                                    {guestAddOn.description?.toUpperCase() ||
                                      '-'}
                                  </Text>
                                </ActivityContent>
                                <Text variant="LabelSmall">
                                  {`Rp ${string2money(guestAddOn.price || 0)}`}
                                </Text>
                              </ActivityContainer>
                            ))}
                          </>
                        )}
                        <SeparatorVertical $margin={24}>
                          <div
                            style={{
                              height: 1,
                              backgroundColor: colors.gray200,
                            }}
                          />
                        </SeparatorVertical>
                      </React.Fragment>
                    );
                  })}
                  {/* {activityGuests.map((activityGuest, idx) => (
                    <SummaryItem
                      key={activityGuest.customId}
                      data={[activityGuest]}
                    />
                  ))} */}
                </>
              )}
              {!!activityAddOns.length && isGroupedPrice && (
                <div>
                  <Text variant="ParagraphSmall" color={colors.gray500}>
                    {t('ticketSales:add_ons')}
                  </Text>
                  {activityAddOns.map((addOns, idx) => {
                    return (
                      <ActivityContainer
                        key={'activity-add-ons' + addOns.id + idx}
                      >
                        <ActivityContent $style={{ paddingLeft: 0 }}>
                          <Text variant="LabelSmall">
                            {[
                              addOns.guestName,
                              (addOns.description || '').toUpperCase(),
                            ]
                              .filter((val) => !!val)
                              .join(' | ')}
                          </Text>
                        </ActivityContent>
                        <Text variant="LabelSmall">
                          {`Rp ${string2money(addOns.price || 0)}`}
                        </Text>
                      </ActivityContainer>
                    );
                  })}
                  <SeparatorVertical $margin={24}>
                    <div
                      style={{ height: 1, backgroundColor: colors.gray200 }}
                    />
                  </SeparatorVertical>
                </div>
              )}
              <SeparatorVertical $margin={16} />
            </React.Fragment>
          );
        })}

        <Total label={t('ticketSales:subtotal_addons')} price={totalAddOns} />
        <Total label={t('common:subtotal')} price={subtotal} />
        <Total label={t('common:discount')} price={discount} />
        <Total label={t('common:other_cost')} price={otherCost} />
        <Total label={t('common:total')} price={total} />
      </DrawerBody>
    </>
  );
}
